import CandlestickSettings from './candlestickSettings'
import LineSettingsDialogs from './lineSettingsDialogs'
import React, { memo, useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ChartType } from '../../proto/generated/chart'
import AreaSettingsDialogs from './areaSettingsDialogs'
import BarSettingsDialogs from './barSettingsDialogs'
import HeikinSettingsDialogs from './heikinSettingsDialogs'
import { updateChartConfig } from '../../reducer/chart.reducer'
import { selectChartConfigSettings, selectChartConfig, selectChartScalesAndLinesSettings, selectChartCanvasSettings, selectChartAlertSettings, selectChartEventsSettings } from '../../selectors/chart'
import { AlertSettings, SeriesOptions, StatusLineSettings } from '../chartV2/chart.typings'
import { AppContext } from '../../App'
import { Button, DialogActions, DialogContent, Divider, Typography, Stack } from '@mui/material'
import DraggableDialogTitle from '../common/DraggableDialogTitle'
import { Dialog } from '@mui/material'
import { ExtraSmallButton } from "../custom";
import ClearIcon from "../../assets/icons/ClearIcon";
import HollowCandlestickSettings from './hollowCandlestickSettings'
import HighLowCandlestickSettings from './highLowCandlestickSettings'
import BaselineSettings from './baselineSettings'
import HlcAreaSettings from './hlcAreaSettings'
import RenkoSettings from './renkoSettings'
import KagiSettings from './kagiSettings'
import PandFSettings from './pandFSettings'
import LinkBreakSettings from './lineBreakSettings'
import VolumeCandlesSettings from './volumeCandleSettings'
import StepLineSettings from './stepLineSettings'
import ColumnSettingsDialogs from './columnSettingsDialogs'
import { defaultTradingSettings, defaultAlertSettings } from '../../util/chart'
import {
    DEFAULT_SERIES_SETTINGS_BY_CHART_TYPE,
    getDefaultChartConfigSettingsPatch,
    getDefaultSeriesSettingsForChartType,
    resolveChartTypeValue,
} from '../../util/chartDefaultSettings'
import { defaultScalesAndLinesSettings } from '../../util/scalesAndLinesDefaults'
import { defaultCanvasSettings } from '../../util/canvasSettingsDefaults'
import { defaultEventsSettings } from '../../util/eventsSettingsDefaults'
import { defaultStatusLineSettings } from '../../util/statusLineDefaults'
import { selectMultiPaneFullScreen } from '../../selectors/common'
import { useFullscreenContainer } from '../../hooks/useFullscreenContainer'
import { WidgetSettingsMenuProvider } from '../widgetDashboard/widgetSettingsMenuContext'

const areStatusLineSettingsEqual = (a: StatusLineSettings, b: StatusLineSettings) => (
    a.logo === b.logo &&
    a.showSymbolName === b.showSymbolName &&
    a.showOHLC === b.showOHLC &&
    a.showBarChange === b.showBarChange &&
    a.showLastDayChange === b.showLastDayChange &&
    a.showExchange === b.showExchange &&
    a.indicatorTitle === b.indicatorTitle &&
    a.indicatorInputs === b.indicatorInputs &&
    a.indicatorValues === b.indicatorValues
)

const DEFAULT_SCALES_AND_LINES_SETTINGS = defaultScalesAndLinesSettings

const DEFAULT_CANVAS_SETTINGS = defaultCanvasSettings

interface Props {
    open: boolean;
    onClose: () => void;
    viewKey: string;
    chartType: ChartType;
}

export const DEFAULT_SETTINGS = DEFAULT_SERIES_SETTINGS_BY_CHART_TYPE

const chartSettingsComponents = (viewKey: string, chartType: ChartType | string) => {
    switch (resolveChartTypeValue(chartType)) {
        case ChartType.CANDLESTICK:
            return <CandlestickSettings viewKey={viewKey} />
        case ChartType.LINE:
            return <LineSettingsDialogs viewKey={viewKey} />
        case ChartType.AREA:
            return <AreaSettingsDialogs viewKey={viewKey} />
        case ChartType.BAR:
            return <BarSettingsDialogs viewKey={viewKey} />
        case ChartType.HEIKIN_ASHI:
            return <HeikinSettingsDialogs viewKey={viewKey} />
        case ChartType.HISTOGRAM:
        case ChartType.COLUMN:
            return <ColumnSettingsDialogs viewKey={viewKey} />
        case ChartType.HOLLOW_CANDLES:
            return <HollowCandlestickSettings viewKey={viewKey} />
        case ChartType.HIGH_LOW:
            return <HighLowCandlestickSettings viewKey={viewKey} />
        case ChartType.BASELINE:
            return <BaselineSettings viewKey={viewKey} />
        case ChartType.HLC_AREA:
            return <HlcAreaSettings viewKey={viewKey} />
        case ChartType.RENKO:
            return <RenkoSettings viewKey={viewKey} />
        case ChartType.KAGI:
            return <KagiSettings viewKey={viewKey} />
        case ChartType.POINT_AND_FIGURE:
            return <PandFSettings viewKey={viewKey} />
        case ChartType.LINE_BREAK:
            return <LinkBreakSettings viewKey={viewKey} />
        case ChartType.VOLUME_CANDLESTICK:
            return <VolumeCandlesSettings viewKey={viewKey} />
        case ChartType.STEP_LINE_CHART:
            return <StepLineSettings viewKey={viewKey} />
        case ChartType.LINE_WITH_MARKERS:
            return <LineSettingsDialogs viewKey={viewKey} />
        default:
            return <></>
    }
}

const ChartSettingsDialogs: React.FC<Props> = ({ open, onClose, viewKey, chartType }) => {
    const dispatch = useDispatch()
    const fullscreenContainer = useFullscreenContainer()
    const appContext = useContext(AppContext)
    const chart = appContext.getChart(viewKey)
    const container = useSelector(selectMultiPaneFullScreen)
    const getDefaultSettings = (type: ChartType) => getDefaultSeriesSettingsForChartType(type)
    const prevSettingsRef = useRef<SeriesOptions>({})
    const statusLineSettings = useSelector(selectChartConfigSettings(viewKey)) ?? defaultStatusLineSettings
    const chartConfig = useSelector(selectChartConfig(viewKey))
    const tradingSettings = chartConfig?.tradingSettings || defaultTradingSettings
    const alertSettings = useSelector(selectChartAlertSettings(viewKey)) ?? defaultAlertSettings
    const scalesAndLinesSettings = useSelector(selectChartScalesAndLinesSettings(viewKey)) ?? DEFAULT_SCALES_AND_LINES_SETTINGS
    const canvasSettings = useSelector(selectChartCanvasSettings(viewKey)) ?? DEFAULT_CANVAS_SETTINGS
    const eventsSettings = useSelector(selectChartEventsSettings(viewKey)) ?? defaultEventsSettings
    const [currentStatusLineSettings, setCurrentStatusLineSettings] = useState<StatusLineSettings>(defaultStatusLineSettings)
    const [prevStatusLineSettings, setPrevStatusLineSettings] = useState<StatusLineSettings>(defaultStatusLineSettings)
    const [currentTradingSettings, setCurrentTradingSettings] = useState(defaultTradingSettings)
    const [prevTradingSettings, setPrevTradingSettings] = useState(defaultTradingSettings)
    const [prevAlertSettings, setPrevAlertSettings] = useState<AlertSettings>(defaultAlertSettings)
    const [prevScalesAndLinesSettings, setPrevScalesAndLinesSettings] = useState<any>(DEFAULT_SCALES_AND_LINES_SETTINGS)
    const [prevCanvasSettings, setPrevCanvasSettings] = useState<any>(DEFAULT_CANVAS_SETTINGS)
    const [prevEventsSettings, setPrevEventsSettings] = useState(defaultEventsSettings)

    useEffect(() => {
        if (open && chart) {
            const settings = chart.primarySeriesGroup?.getPrimarySeries()?.getDefaultOptions()
            // `priceLineVisible` / `lastValueVisible` are owned by the global scales-and-lines
            // settings (showSymbolLine / showSymbolValue). Don't capture them here, otherwise
            // restoring on cancel would persist them into the per-series options and override
            // the global toggle.
            const { priceLineVisible, lastValueVisible, ...rest } = { ...(settings ?? getDefaultSettings(chartType)) } as SeriesOptions
            prevSettingsRef.current = rest
        }
    }, [open])

    useEffect(() => {
        if (open && chart) {
            const appliedStatusLineSettings = statusLineSettings
            const appliedTradingSettings = tradingSettings
            setCurrentStatusLineSettings((prev) =>
                areStatusLineSettingsEqual(prev, appliedStatusLineSettings) ? prev : (appliedStatusLineSettings as any)
            )
            setPrevStatusLineSettings((prev) =>
                areStatusLineSettingsEqual(prev, appliedStatusLineSettings) ? prev : (appliedStatusLineSettings as any)
            )
            setCurrentTradingSettings((prev) => (prev === appliedTradingSettings ? prev : (appliedTradingSettings as any)))
            setPrevTradingSettings((prev) => (prev === appliedTradingSettings ? prev : (appliedTradingSettings as any)))
            setPrevAlertSettings(alertSettings)
            setPrevScalesAndLinesSettings(scalesAndLinesSettings)
            setPrevCanvasSettings(canvasSettings)
            setPrevEventsSettings({ ...defaultEventsSettings, ...eventsSettings })
        }
    }, [open, chartType, statusLineSettings, tradingSettings, alertSettings, scalesAndLinesSettings, canvasSettings, eventsSettings, chart])

    const handleApplyDefault = () => {
        const defaultSettings = getDefaultSettings(chartType)
        const configPatch = getDefaultChartConfigSettingsPatch()
        if (chart) {
            chart.chartConfig = {
                ...chart.chartConfig,
                ...configPatch,
            }
            const primarySeries = chart.primarySeriesGroup?.getPrimarySeries()
            if (primarySeries) {
                primarySeries.updateOptions(defaultSettings as SeriesOptions)
            }
        }
        dispatch(updateChartConfig({
            viewKey,
            config: configPatch,
            createIfAbsent: false,
        }))
        setCurrentStatusLineSettings(configPatch.statusLineSettings as any)
        setPrevStatusLineSettings(configPatch.statusLineSettings as any)
        setCurrentTradingSettings(configPatch.tradingSettings as any)
        setPrevTradingSettings(configPatch.tradingSettings as any)
        setPrevAlertSettings(configPatch.alertSettings)
        setPrevScalesAndLinesSettings(configPatch.scalesAndLinesSettings)
        setPrevCanvasSettings(configPatch.canvasSettings)
        setPrevEventsSettings(configPatch.eventsSettings)
    }

    const handleCancel = () => {
        if (!chart) return
        dispatch(updateChartConfig({
            viewKey,
            config: {
                statusLineSettings: prevStatusLineSettings,
                tradingSettings: prevTradingSettings,
                alertSettings: prevAlertSettings,
                scalesAndLinesSettings: prevScalesAndLinesSettings,
                canvasSettings: prevCanvasSettings,
                eventsSettings: prevEventsSettings,
            },
            createIfAbsent: false
        }))
        if (chart) {
            const primarySeries = chart.primarySeriesGroup?.getPrimarySeries()
            if (primarySeries) {
                primarySeries.updateOptions(prevSettingsRef.current as SeriesOptions)
            }
        }
        setCurrentStatusLineSettings(prevStatusLineSettings)
        setCurrentTradingSettings(prevTradingSettings)
        onClose()
    }

    const handleOk = () => {
        setPrevStatusLineSettings(currentStatusLineSettings)
        setPrevTradingSettings(currentTradingSettings)
        onClose()
    }
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            container={fullscreenContainer ?? undefined}
            hideBackdrop
            disableEnforceFocus
            disableScrollLock
            sx={{ 
                pointerEvents: 'none',
                zIndex: 99999
            }}
            PaperProps={{
                sx: {
                    minHeight: "40%",
                    maxHeight: 'max-content',
                    minWidth: '500px',
                    pointerEvents: 'all',
                }
            }}
        >
            <DraggableDialogTitle>
                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                    <Typography variant={'h6'}>Settings</Typography>
                    <ExtraSmallButton onClick={onClose} aria-label="close" color={"inherit"}>
                        <ClearIcon />
                    </ExtraSmallButton>
                </Stack>
            </DraggableDialogTitle>
            <DialogContent sx={{ padding: "0px", display: "flex", flexDirection: "column", height: '100%' }}>
                <WidgetSettingsMenuProvider>
                    {chartSettingsComponents(viewKey, chartType) || <div>No settings available</div>}
                </WidgetSettingsMenuProvider>
            </DialogContent>
            <Divider />
            <DialogActions>
                <Button variant="outlined" onClick={handleApplyDefault} sx={{ marginRight: 'auto' }}>
                    Apply Default
                </Button>
                <Button variant="outlined" onClick={handleCancel}>
                    Cancel
                </Button>
                <Button onClick={handleOk} variant="contained">
                    Ok
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default memo(ChartSettingsDialogs)